import type {
  ClobApiKeyResponse,
  ClobHealthResponse,
  ClobMarket,
  ClobOrder,
  ClobTrade,
  EscrowBalance,
  OrderbookSnapshot,
  SignedOrder,
} from './types';
import { CLOB_API_URL } from '@/client/confs/app';

class ClobClient {
  private baseUrl: string;
  private apiKey: string | null = null;
  // Fail-fast when no CLOB backend is configured for this tenant (e.g. AIP
  // prod has no VITE_CLOB_API_URL): reject before touching fetch, instead of
  // letting requests go out to a relative empty-string URL and hit the SPA
  // fallback HTML.
  private disabled: boolean;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl || CLOB_API_URL;
    this.disabled = !this.baseUrl;
    if (this.disabled) {
      console.warn('[clob] disabled: CLOB_API_URL not configured');
    }
  }

  setApiKey(key: string) {
    this.apiKey = key;
  }

  clearApiKey() {
    this.apiKey = null;
  }

  private async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    if (this.disabled) {
      throw new Error('CLOB API not configured');
    }
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };
    if (this.apiKey) {
      headers['X-Api-Key'] = this.apiKey;
    }
    const response = await fetch(`${this.baseUrl}${path}`, {
      ...options,
      headers,
    });
    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(`CLOB API error ${response.status}: ${text}`);
    }
    return response.json();
  }

  async health(): Promise<ClobHealthResponse> {
    return this.request('/health');
  }

  async getMarkets(): Promise<ClobMarket[]> {
    const raw = await this.request<ClobMarket[] | { markets: string[] }>('/markets');
    // The deployed match-engine may return the legacy format: { markets: string[] }
    // where each string is a token ID. Normalize to ClobMarket[].
    if (Array.isArray(raw)) return raw;
    if ('markets' in raw && Array.isArray(raw.markets)) {
      return raw.markets.map(tokenId => ({
        conditionId: tokenId,
        questionId: '',
        tokens: [{ tokenId, outcome: 'YES' }],
        status: 'active',
        createdAt: '',
      }));
    }
    return [];
  }

  async getOrderbook(tokenId: string): Promise<OrderbookSnapshot> {
    const raw = await this.request<any>(`/book/${tokenId}`);
    // Normalize: deployed API returns { price, quantity } per level;
    // frontend expects { price, size }.
    const normalizeLevel = (l: any) => ({
      price: l.price,
      size: l.size ?? l.quantity ?? '0',
    });
    return {
      bids: (raw.bids ?? []).map(normalizeLevel),
      asks: (raw.asks ?? []).map(normalizeLevel),
      timestamp: raw.timestamp ?? 0,
    };
  }

  async getPrice(tokenId: string): Promise<{ bid: string; ask: string }> {
    return this.request(`/price/${tokenId}`);
  }

  async authenticate(address: string, signature: string, timestamp: number): Promise<ClobApiKeyResponse> {
    return this.request('/auth/api-key', {
      method: 'POST',
      body: JSON.stringify({ address, signature, timestamp }),
    });
  }

  async placeOrder(order: SignedOrder): Promise<{ orderId: string }> {
    return this.request('/order', {
      method: 'POST',
      body: JSON.stringify(order),
    });
  }

  async cancelOrder(orderId: string): Promise<void> {
    return this.request(`/order/${orderId}`, {
      method: 'DELETE',
    });
  }

  async getOrders(): Promise<ClobOrder[]> {
    return this.request('/orders');
  }

  async getTrades(): Promise<ClobTrade[]> {
    return this.request('/trades');
  }

  async getEscrowBalance(): Promise<EscrowBalance> {
    return this.request('/escrow/balance');
  }
}

export const clobClient = new ClobClient();
